import {
  Banner,
  Layout,
  Card,
  EmptyState,
  Link,
  PageActions,
  FooterHelp,
} from '@shopify/polaris';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';

import {resetServiceSelection} from '../../actions/progress';
import {isTestMode, disableTestMode} from './helpers/test_mode';
import {previousStep} from '../../actions/steps';
import {setServiceHandle} from '../../actions/service';
import {submitStep} from './helpers/step';
import Navigation from './navigation';
import OrganizationCard from './organizationCard';
import Question from './question';
import store from '../../store';
import Header from './header';
import {MobilePadding} from 'components/polaris/react/mobile-padding';

class Step extends React.Component {
  focusRef = React.createRef();

  focusOnStepChange = () => {
    if (!this.focusRef.current) return;
    this.focusRef.current.focus();
  };

  constructor(props) {
    super(props);

    this.renderNavigation = this.renderNavigation.bind(this);
    this.renderOrganizationCard = this.renderOrganizationCard.bind(this);
    this.renderLayout = this.renderLayout.bind(this);
    this.renderPageActions = this.renderPageActions.bind(this);
    this.renderFooterHelp = this.renderFooterHelp.bind(this);
  }

  componentDidMount() {
    this.focusOnStepChange();
  }

  componentDidUpdate() {
    this.focusOnStepChange();
  }

  render() {
    const {step} = this.props;
    const {questions: questionIds} = step;

    if (questionIds.length) {
      return [
        this.renderTestModeBanner(),
        this.renderHeader(),
        this.renderOrganizationCard(),
        this.renderNavigation(),
        this.renderLayout(),
        this.renderPageActions(),
        this.renderFooterHelp(),
      ];
    } else {
      return renderEmptyState();
    }
  }

  renderTestModeBanner() {
    if (!isTestMode()) {
      return null;
    }

    return (
      <Layout.Section key="test-mode-banner">
        <Banner
          title="Test mode"
          status="warning"
          action={{
            content: 'Disable test mode',
            onClick: disableTestMode,
          }}
        >
          <p>
            You are currently in test mode, your brief will not actually contact
            a real partner. If you need to submit an actual job request you can
            disable test mode
          </p>
        </Banner>
      </Layout.Section>
    );
  }

  renderHeader() {
    return (
      <Layout.Section key="header">
        <MobilePadding>
          <Header />
        </MobilePadding>
      </Layout.Section>
    );
  }

  renderNavigation() {
    return (
      <Layout.Section key="navigation">
        <MobilePadding>
          <div ref={this.focusRef} tabIndex={-1} className="reset-focus">
            <Navigation />
          </div>
        </MobilePadding>
      </Layout.Section>
    );
  }

  renderLayout() {
    const {step} = this.props;
    const {
      entities: {questions},
    } = store.getState();
    const {questions: questionIds} = step;
    const groupedQuestions = questionIds
      .map(id => questions[id])
      .filter(question => !question.hidden)
      .reduce(groupQuestions, [])
      .filter(group => group.length);
    const sectionKey = 'step-content';

    const questionMarkup = groupedQuestions.map((questionGroup, idx) => {
      /* eslint-disable react/no-array-index-key */
      return (
        <Card key={`${step.handle}-${idx}`}>
          {questionGroup.map(questionId => {
            return <Question key={questionId} questionId={questionId} />;
          })}
        </Card>
      );
      /* eslint-enable react/no-array-index-key */
    });

    return (
      <Layout.AnnotatedSection
        title={step.title}
        description={step.description}
        key={sectionKey}
      >
        {questionMarkup}
      </Layout.AnnotatedSection>
    );
  }

  renderOrganizationCard() {
    return this.props.organization ? (
      <Layout.Section key="organizationCard">
        <OrganizationCard />
      </Layout.Section>
    ) : null;
  }

  resetServiceSelection() {
    this.props.setServiceHandle(this.props.previousServiceHandle);
    this.props.resetServiceSelection();
  }

  renderPageActions() {
    const {currentStepId, currentStepIndex} = this.props;

    const secondaryActions = [
      {
        content: 'Back',
        onClick: () => {
          if (currentStepIndex === 0) {
            this.resetServiceSelection();
          } else {
            this.props.previousStep();
          }
        },
      },
    ];

    const primaryAction = {
      content: 'Next',
      onClick: () => {
        submitStep(currentStepId);
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      },
    };

    return (
      <Layout.Section key="page-actions">
        <PageActions
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
        />
      </Layout.Section>
    );
  }

  renderFooterHelp() {
    return (
      <Layout.Section key="footer-help">
        <FooterHelp>
          Learn more about{' '}
          <Link
            url={
              'https://help.shopify.com/en/manual/community/experts/experts-faq'
            }
            external
          >
            {'working with an expert'}
          </Link>
        </FooterHelp>
      </Layout.Section>
    );
  }
}

function groupQuestions(questions, question) {
  const len = questions.length === 0 ? 0 : questions.length - 1;

  questions[len] = questions[len] || [];
  questions[len].push(question.handle);

  if (question.endsGroup === true) {
    questions.push([]);
  }

  return questions;
}

function renderEmptyState() {
  return (
    <EmptyState
      heading="Oops, something went wrong"
      action={{content: 'Try again', onClick: () => location.reload()}}
      secondaryAction={{}}
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
    >
      <p>It seems like there was a problem fetching the next step.</p>
    </EmptyState>
  );
}

function mapStateToProps(state, ownProps) {
  const {
    progress: {currentStepId, currentStepIndex},
    entities: {steps, services},
    context: {organization, previousServiceHandle, serviceHandle},
  } = state;
  const step = steps[ownProps.stepId];

  return {
    currentStepId,
    currentStepIndex,
    step,
    organization,
    previousServiceHandle,
    services,
    serviceHandle,
  };
}

const mapDispatchToProps = {
  previousStep,
  setServiceHandle,
  resetServiceSelection,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Step);

Step.propTypes = {
  stepId: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
  currentStepId: PropTypes.string.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  step: PropTypes.object.isRequired,
  previousStep: PropTypes.func.isRequired,
  setServiceHandle: PropTypes.func.isRequired,
  organization: PropTypes.object,
  previousServiceHandle: PropTypes.string,
};
